<template>
    <div>
        <div v-if="!loading && (!submissions.length || !carouselSubmission)" class="alert alert-warning col-sm-6 offset-sm-3" role="alert">
            No submissions in queue!
        </div>
        <!-- mobile -->
        <div v-if="!loading && carouselSubmission" :class="[carousel ? 'd-block d-sm-none' : 'd-none']">
            <transition :name="transition">
                <mobile-submission-card
                    :id="carouselSubmission.id"
                    :text="carouselSubmission.text"
                    :image_url="carouselSubmission.image_url"
                    :status="carouselSubmission.status"
                    :submission_time="carouselSubmission.submission_time"
                    :hit_filter="carouselSubmission.filter_hit_id ? true : false"
                    :secondOp="carouselSubmission.secondOp ? true : false"
                    :trialEditor="carouselSubmission.trialEditor ? true : false"
                    :secondary-pages="secondaryPages"
                    :route_truth_details="carouselSubmission.route_truth_details"
                    :header_text="carouselSubmission.header_text"
                    :numbered="carouselSubmission.numbered ? true : false"
                    :nsfw_probability="carouselSubmission.nsfw_probability"
                    @submission-dismissed="dismissSubmission"
                    @open-edit-modal="openEditModal"
                    @open-move-modal="openMoveModal"
                    :mobile="true"
                    :index="carouselIndex"
                    :indexMax="submissions.length"
                    @decrement-carousel-index="decrementCarouselIndex()"
                    @increment-carousel-index="incrementCarouselIndex()"
                    :key="carouselIndex"
                    v-touch:swipe.left="incrementCarouselIndex" 
                    v-touch:swipe.right="decrementCarouselIndex"
                />
            </transition>

            <sub-buttons
                :id="carouselSubmission.id"
                :text="carouselSubmission.text"
                :image_url="carouselSubmission.image_url"
                :secondary-pages="carouselSubmission.secondaryPages"
                :route_truth_details="carouselSubmission.route_truth_details"
                :mobile="true"
                :instagram="instagram"
                @submission-dismissed="dismissSubmission"
                @open-edit-modal="openEditModal"
                @open-move-modal="openMoveModal"
            />
        </div>

        <!-- non-mobile -->
        <div class="row justify-content-md-center justify-content-left" :class="[carousel ? 'd-none d-sm-block' : 'd-block']">
            <div class="col-12 subListTop mb-3 col-xl-8 offset-xl-2 col-lg-10 offset-lg-1">
                <v-card class="mt-4">
                    <v-expansion-panels accordion multiple>
                        <v-expansion-panel>
                            <v-expansion-panel-header>
                                <template v-slot:default="{ open }">
                                    <v-row no-gutters>
                                        <v-col cols="2">
                                            Filters
                                        </v-col>
                                        <v-col cols="10" class="text--secondary">
                                            <v-fade-transition leave-absolute>
                                                <div v-if="!open">
                                                    <span>
                                                        {{ filterSentence }}
                                                    </span>
                                                </div>
                                            </v-fade-transition>
                                        </v-col>
                                    </v-row>
                                </template>
                            </v-expansion-panel-header>
                            <v-expansion-panel-content>
                                <v-row>
                                    <v-col cols="12" sm="6">
                                        <v-select
                                            :label="'Status'"
                                            :items="searchParamOptions.status"
                                            item-value="value"
                                            item-text="label"
                                            v-model="searchParams.status"
                                            outlined
                                            chips
                                            multiple
                                            deletable-chips
                                            hide-details 
                                        />
                                    </v-col>
                                    <v-col cols="12" sm="6">
                                        <v-select
                                            :label="'Image'"
                                            :items="searchParamOptions.image"
                                            item-value="value"
                                            item-text="label"
                                            v-model="searchParams.image"
                                            outlined
                                            chips
                                            multiple
                                            deletable-chips
                                        />
                                    </v-col>
                                </v-row>
                            </v-expansion-panel-content>
                        </v-expansion-panel>
                        <v-expansion-panel>
                            <v-expansion-panel-header>
                                <template v-slot:default="{ open }">
                                    <v-row no-gutters>
                                        <v-col cols="2">
                                            Sorting
                                        </v-col>
                                        <v-col cols="10" class="text--secondary">
                                            <v-fade-transition leave-absolute>
                                                <div v-if="!open">
                                                    <span>
                                                        {{ sortingSentence }}
                                                    </span>
                                                </div>
                                            </v-fade-transition>
                                        </v-col>
                                    </v-row>
                                </template>
                            </v-expansion-panel-header>
                            <v-expansion-panel-content>
                                <v-col cols="12" sm="6">
                                    <v-select
                                        :label="'Order by'"
                                        :items="searchParamOptions.direction"
                                        item-value="value"
                                        item-text="label"
                                        v-model="searchParams.direction"
                                        outlined
                                        hide-details
                                    />
                                </v-col>
                            </v-expansion-panel-content>
                        </v-expansion-panel>
                        <v-divider />
                        <!-- if this blank panel isn't here the divider doesn't show -->
                        <v-expansion-panel>
                        </v-expansion-panel>
                    </v-expansion-panels>
                    <v-card-actions>
                        <v-spacer />
                        <v-btn class="primary" @click="refetchSubmissions" :disabled="!canFetch">
                            Fetch
                        </v-btn>
                    </v-card-actions>
                </v-card>
                <template v-if="!loading">
                    <sorting-list-item v-for="submission in submissions" v-bind:key="submission.id"
                        :id="submission.id"
                        :text="submission.text"
                        :status="submission.status"
                        :image_url="submission.image_url"
                        :submission_time="submission.submission_time"
                        :filter_phrases="submission.filter_phrases"
                        :secondary-pages="secondaryPages"
                        :route_truth_details="submission.route_truth_details"
                        :header_text="submission.header_text"
                        :numbered="submission.numbered ? true : false"
                        :nsfw_probability="submission.nsfw_probability"
                        :instagram="instagram"
                        @submission-dismissed="dismissSubmission"
                        @open-edit-modal="openEditModal"
                        @open-move-modal="openMoveModal"
                    />
                </template>
                <div v-else class="text-center">
                    <i class="fa fa-spinner fa-pulse fa-fw fa-3x"></i>
                </div>
            </div>

            <nav v-if="!loading && pagination.last_page > 1" aria-label="Page navigation">
                <ul class="pagination mb-0">
                    <li v-bind:class="[{disabled: !pagination.prev_page_url}]" class="page-item"><a class="page-link" href="#" @click="fetchSubmissions(pagination.prev_page_url)">Previous</a></li>

                    <li class="page-item btn-group dropup">
                        <button type="button" class="btn page-link text-dark dropdown-toggle" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                            Page {{ pagination.current_page }} of {{ pagination.last_page }}
                        </button>
                        <div class="dropdown-menu">
                            <a v-for="paginationPage in paginationPages" :key="paginationPage.page" 
                               class="dropdown-item font-weight-normal" href="#" @click="fetchSubmissions(paginationPage.link)">
                               Page {{ paginationPage.page }}
                            </a>
                        </div>
                    </li>

                    <li v-bind:class="[{disabled: !pagination.next_page_url}]" class="page-item"><a class="page-link" href="#" @click="fetchSubmissions(pagination.next_page_url)">Next</a></li>
                </ul>
            </nav>
        </div>

        <edit-submission-popup
            ref="editmodal"
            :submission="activesubmission"
            @submission-text-changed="changeSubmissionText"
        />
        <move-submission-page-popup
            ref="movemodal"
            :submission="activesubmission"
            :secondary-pages="secondaryPages"
            @submission-dismissed="dismissSubmission"
        />
    </div>
</template>


<script>
import EditSubmissionPopup from '../EditSubmissionPopup'
import MoveSubmissionPagePopup from '../MoveSubmissionPagePopup'
import MobileSubmissionCard from './MobileSubmissionCard'
import SubButtons from './SubButtons'
import SortingListItem from './SortingListItem'

export default {
    name: "SortingList",

    components: {
        EditSubmissionPopup,
        MoveSubmissionPagePopup,
        MobileSubmissionCard,
        SubButtons,
        SortingListItem
    },

    props: {
        order: {
            type: String,
            required: false,
            default: "asc",
        },
        carousel: {
            type: Boolean,
            required: false,
            default: true,
        },
        canApproveSecondOpinion: {
            type: Boolean,
            required: false,
            default: false,
        },
        canApproveEditor: {
            type: Boolean,
            required: false,
            default: false,
        }
    },

    data() {
        return {
            loading: false,
            submissions: [],
            pagination: [],
            activesubmission: {
                id: undefined,
                text: '',
            },
            secondaryPages: [],
            carouselIndex: 0,
            direction: 1,
            instagram: null,
            searchParams: {
                direction: "desc",
                status: [
                    "Submitted",
                    "2ndOpinion",
                    "EditorOp",
                ],
                image: [0, 1],
            },
            searchParamOptions: {
                status: [
                    {
                        value: 'Submitted',
                        label: 'Submitted'
                    },
                    {
                        value: '2ndOpinion',
                        label: 'Second Opinion Requested'
                    },
                    {
                        value: 'EditorOp',
                        label: 'Approved by a Trial Editor'
                    },
                ],
                image: [
                    {
                        value: 1,
                        label: 'With Image'
                    },
                    {
                        value: 0,
                        label: 'Without Image'
                    }
                ],
                direction: [
                    {
                        value: 'desc',
                        label: 'Newest First'
                    },
                    {
                        value: 'asc',
                        label: 'Oldest First'
                    }
                ]
            }
        }
    },

    computed: {
        filterSentence() {
            if (this.searchParams.status.length === 0 || this.searchParams.image.length === 0) {
                return "No valid submissions to fetch";
            }
            const statusMap = {
                "Submitted": "submitted",
                "2ndOpinion": "second opinion requested",
                "EditorOp": "approved by a trial editor",
            };
            let statuses = this.searchParams.status.map(x => statusMap[x]);
            let statusSentence = "";
            switch (statuses.length) {
                case 1:
                    statusSentence = statuses[0];
                    break;
                case 2:
                    statusSentence = `${statuses[0]} and ${statuses[1]}`;
                    break;
                case 3:
                    statusSentence = `${statuses[0]}, ${statuses[1]}, and ${statuses[2]}`;
                    break;
            }
            statusSentence = statusSentence.charAt(0).toUpperCase() + statusSentence.slice(1);
            let imageSentence = ""
            if (!this.searchParams.image.includes(0)) {
                imageSentence = "only with images";
            } else if (!this.searchParams.image.includes(1)) {
                imageSentence = "only without images";
            } else {
                return statusSentence;
            }
            return `${statusSentence}, ${imageSentence}`;
        },
        sortingSentence() {
            switch (this.searchParams.direction) {
                case "asc":
                    return "Oldest first";
                case "desc":
                    return "Newest first";
            }
        },
        canFetch() {
            return !this.loading
                && this.searchParams.status.length > 0
                && this.searchParams.image.length > 0;
        },
        paginationPages: function() {
            var pages = [];
            if (this.pagination.last_page < 51)
            {
                for (let index = 1; index <= this.pagination.last_page; index++) {
                    pages.push({
                        page: index,
                        link: this.pagination.path + "?page=" + index,
                    });
                }
            }
            else {
                for (let index = 1; index <= this.pagination.last_page; index = Math.round((index+20)/10)*10) {
                    pages.push({
                        page: index,
                        link: this.pagination.path + "?page=" + index,
                    });
                }
            }
            return pages;
        },
        carouselSubmission: function() {
            return this.submissions[this.carouselIndex]
        },
        transition: ({ direction }) => 
            direction > 0 ? 'fade out-left': 
            'fade out-right',
    },

    created() {
        let retrievedParams = JSON.parse(localStorage.getItem("searchParams")) ?? {};
        // when deconstructed this way, any clashes favour the second object
        // i.e. params retrieved from local storage take precedence
        this.searchParams = {...this.searchParams, ...retrievedParams};

        if (!this.canApproveSecondOpinion) {
            this.searchParams.status = this.searchParams.status.filter(x => x != "2ndOpinion");
            this.searchParamOptions.status = this.searchParamOptions.status.filter(x => x.value != "2ndOpinion");
        }

        if (!this.canApproveEditor) {
            this.searchParams.status = this.searchParams.status.filter(x => x != "EditorOp");
            this.searchParamOptions.status = this.searchParamOptions.status.filter(x => x.value != "EditorOp");
        }

        this.fetchSubmissions();
        this.fetchSecondaryPages();
        this.loadPageDetails()
    },

    methods: {
        refetchSubmissions() {
            return this.fetchSubmissions();
        },

        fetchSubmissions(page_url) {
            if (!this.canFetch) {
                return;
            }
            this.loading = true;
            page_url = page_url || '/api/queues/sorting';
            axios.get(page_url, {
                params: this.searchParams
            })
            .then(response => {
                this.submissions = Object.values(response.data.data); 
                this.makePagination(response.data);
                localStorage.setItem('searchParams', JSON.stringify(this.searchParams));
            })
            .catch(error => {
                console.log(error);
            })
            .finally(() => {
                this.loading = false;
            });
        },

        fetchSecondaryPages(page_url) {
            axios.get('/api/admins/pages/secondary')
            .then(response => {
                // convert to an array (so we can easily get the length).
                this.secondaryPages = Object.values(response.data); 
            })
            .catch(error => {
                console.log(error);
            });
        },
        loadPageDetails(page_url) {
            var self = this;
            axios.get('/api/admins/pages/this')
            .then(function (response) {
                self.instagram = Boolean(response.data.instagram_id);
            })
            .catch(function (error) {
                console.log(error);
            });
        },

        makePagination(response) {
            let pagination = {
                current_page: response.current_page,
                last_page: response.last_page,
                next_page_url: response.next_page_url,
                prev_page_url: response.prev_page_url,
                path: response.path,
            };
            this.pagination = pagination;
        },

        dismissSubmission(submission)
        {
            //this is messy but it'll do for now..?
            let i = this.submissions.map(item => item.id).indexOf(submission.id) // find index of your object
            if(i != -1)
            {
                this.submissions.splice(i, 1) // remove it from array
                this.updateBottomIfValidOrRefreshAll()
            }
            else
            {
                console.log("Error, couldn't find submission in any list!")
            }
            this.$forceUpdate(); //udpate computed
            $(".tooltip").tooltip("hide"); //remove all tooltips
        },

        updateBottomIfValidOrRefreshAll()
        {
            this.fetchLast2Submissions(this.pagination).then(bottomSubs => {
                if (bottomSubs.length >= 2)
                {
                    if (bottomSubs[0].id == this.submissions[this.submissions.length - 1].id)
                    {
                        this.submissions.push(bottomSubs[1]);
                    }
                    else if (bottomSubs[1].id == this.submissions[this.submissions.length - 1].id && bottomSubs[0].id == this.submissions[this.submissions.length - 2].id)
                    {
                        //no difference, so don't do anything!
                    }
                    else
                    {
                        this.fetchSubmissions();
                    }
                }
            });
        },
        fetchLast2Submissions:(pagination) => //return a promise (to go via ajax...? https://stackoverflow.com/questions/55071308/how-to-return-a-response-from-axios-in-vue, https://stackoverflow.com/questions/50395181/js-promise-in-vue-shows-as-undefined)
        {
            let bottomSubs = null;
            return axios.get('/api/queues/sorting/lastofpage', {
                params: {
                    page: pagination.current_page,
                    count: 2,
                    orderDirection: this.searchParams.direction,
                }
            })
            .then(response => {
                return Object.values(response.data);
            })
            .catch(error => {
                console.log(error);
            });
        },


        openMoveModal(submissionId, submissionText)
        {
            this.activesubmission.id = submissionId;
            this.activesubmission.text = submissionText;
            this.$refs.movemodal.show();
        },

        openEditModal(submissionId, submissionText)
        {
            this.activesubmission.id = submissionId;
            this.activesubmission.text = submissionText;
            this.$refs.editmodal.show();
        },
        changeSubmissionText(submission)
        {
            //this is messy but it'll do for now..?
            let i = this.submissions.map(item => item.id).indexOf(submission.id) // find index of your object
            if(i != -1)
            {
                this.submissions[i].text = submission.text;
            }
            else
            {
                console.log("Error, couldn't find submission in any list!")
            }
        },

        incrementCarouselIndex()
        {
            if (this.carouselIndex + 1 < this.submissions.length)
            {
                this.carouselIndex = this.carouselIndex + 1; 
                this.direction = 1;
            }
        },
        decrementCarouselIndex()
        {
            if (this.carouselIndex - 1 >= 0)
            {
                this.carouselIndex = this.carouselIndex - 1; 
                this.direction = -1;
            }
        }
    }
}
</script>

<style scoped>
    .v-expansion-panel:before { box-shadow: none !important; } 
</style>